import { centsToDollars, unitPriceDollars } from "./launchAddOnPricing"

// Amplitude already holds `web_pos:undefined`, `web_pos:[object PointerEvent]`
// and `dry_stack_schedule_view:undefined` — each one a place where a variable
// reached a tracking call instead of a name. Those events are permanent and
// cannot be merged back into what they were meant to be.
//
// So every name below is written out in full rather than assembled from a
// prefix and a suffix at the call site. The surface picks a branch of this
// object; it never builds a string. Nothing here interpolates.
export const BOATER = "boater"
export const MARINA = "marina"

export const ADDON_EVENTS = {
  [BOATER]: {
    added: "dry_stack_boater_view:launch_addon_added",
    removed: "dry_stack_boater_view:launch_addon_removed",
    quantityChanged: "dry_stack_boater_view:launch_addon_quantity_changed",
    fuelModeChosen: "dry_stack_boater_view:launch_addon_fuel_mode_chosen",
  },
  [MARINA]: {
    added: "dry_stack_schedule_view:launch_addon_added",
    removed: "dry_stack_schedule_view:launch_addon_removed",
    quantityChanged: "dry_stack_schedule_view:launch_addon_quantity_changed",
    fuelModeChosen: "dry_stack_schedule_view:launch_addon_fuel_mode_chosen",
  },
}

// Its own event rather than a property on new_launch_started, which fires
// before the catalog is known on both surfaces. See useAddonOptionsShown.
export const ADDON_OPTIONS_SHOWN_EVENT = {
  [BOATER]: "dry_stack_boater_view:launch_addon_options_shown",
  [MARINA]: "dry_stack_schedule_view:launch_addon_options_shown",
}

// The marina modal saves both a new launch and an edit to an existing one, and
// built its event name by interpolating the tab: `${currentTabName()}_tab_edit`.
// currentTabName() indexes a three-element array, so an out-of-range tab put a
// literal `undefined` in the name — which is how Amplitude came to hold
// `dry_stack_schedule_view:undefined`, permanently and unmergeably.
//
// Every variant is written out here instead. An unrecognized tab lands in a
// named bucket rather than poisoning the taxonomy with a fresh `undefined`.
const SCHEDULE_SAVED_VARIANTS = {
  new: "dry_stack_schedule_view:new_launch_saved",
  scheduled: "dry_stack_schedule_view:scheduled_tab_edit_launch_saved",
  launched: "dry_stack_schedule_view:launched_tab_edit_launch_saved",
  returned: "dry_stack_schedule_view:returned_tab_edit_launch_saved",
  unknown: "dry_stack_schedule_view:unknown_tab_edit_launch_saved",
}

const SCHEDULE_NOTE_VARIANTS = {
  new: "dry_stack_schedule_view:new_launch_note_added",
  scheduled: "dry_stack_schedule_view:scheduled_tab_edit_launch_note_added",
  launched: "dry_stack_schedule_view:launched_tab_edit_launch_note_added",
  returned: "dry_stack_schedule_view:returned_tab_edit_launch_note_added",
  unknown: "dry_stack_schedule_view:unknown_tab_edit_launch_note_added",
}

export const scheduleLaunchSavedEvent = (variant) =>
  SCHEDULE_SAVED_VARIANTS[variant] ?? SCHEDULE_SAVED_VARIANTS.unknown

export const scheduleLaunchNoteAddedEvent = (variant) =>
  SCHEDULE_NOTE_VARIANTS[variant] ?? SCHEDULE_NOTE_VARIANTS.unknown

// The value of the `surface` property, as opposed to the key used to pick a
// name above. Sent explicitly rather than inferred from a route, a flag, or a
// user role, and deliberately not parsed back out of the group prefix.
export const SURFACE_PROPERTY = {
  [BOATER]: "boater_web",
  [MARINA]: "marina_web",
}

// The count of launch-eligible items on the list when the form was opened —
// not the size of the marina's POS catalog, which runs to 870 items at some
// accounts. Zero is meaningful: it says the marina offered nothing, which is
// what separates a low attach rate from a marina that configured nothing.
export const addonOptionsShown = (catalog) => (catalog ?? []).length

// Dollars, not the raw minor-unit integer. Fuel is stored in hundredths of a
// cent and everything else in cents, so sending price_per_unit as it arrives
// would make fuel read 100x high in any chart that mixes item types.
const priceDollars = (item) => {
  const price = unitPriceDollars(item)

  return price === null ? null : price.toNumber()
}

// The seven-field item shape, identical on every event that names an item, so
// one query shape works across all of them.
//
// fuel_unit is the eighth and is not in the measurement plan: it postdates it.
// A marina now sells fuel by the gallon or the liter (MOB-1084), so a bare
// `quantity: 20` on a fuel line is two different amounts at two marinas and a
// pooled volume chart would be meaningless without it. Null on non-fuel, which
// is a count rather than a measured amount.
export const itemProperties = (item, selection = {}) => ({
  product_id: item.productId,
  item_name: item.name,
  item_type: item.productType ?? null,
  category: item.category ?? null,
  quantity: selection.quantity ?? null,
  price: priceDollars(item),
  // Present and null on anything that is not fuel, rather than absent, so the
  // field is on every item. Carries the backend value (`fill_up` / `gallons`),
  // never the on-screen label — which is now the marina's own wording and
  // stays `gallons` even where the marina measures in liters.
  fuel_mode: selection.fuelMode ?? null,
  fuel_unit: item.fuelUnit?.symbol ?? null,
})

// One entry per line on the request, for the events that describe a whole
// request rather than a single item.
export const itemsProperty = (catalog, selections) =>
  selections
    .map((selection) => {
      const item = catalog.find(
        (candidate) => candidate.productId === selection.productId
      )

      return item ? itemProperties(item, selection) : null
    })
    .filter(Boolean)

// The same shape again, built from an already-requested add-on as the launch
// row serves it (Manage::DryStack::LaunchAddonSerializer) rather than from a
// catalog item plus a selection. One shape, two sources: the picker knows a
// product and what was picked, a saved launch knows only the line.
export const requestedItemProperties = (addon) => ({
  product_id: addon.productId,
  item_name: addon.name,
  item_type: addon.productType ?? null,
  category: addon.category ?? null,
  quantity: addon.quantity ?? null,
  price: priceDollars(addon),
  fuel_mode: addon.fuelMode ?? null,
  fuel_unit: addon.fuelUnitSymbol ?? null,
})

export const requestedItemsProperty = (addons) =>
  (addons ?? []).map(requestedItemProperties)

// What the person was quoted at that moment, in dollars — the same unit each
// item's `price` is in, so summing items[] and comparing to this is a valid
// question to ask a chart. The server totals in cents.
//
// Not revenue: a full tank has no total at all until the pump stops, and
// DryStack::LaunchAddonEstimateDecorator drops every row when nothing in the
// selection is priceable — so this is genuinely null there rather than zero.
export const estimatedTotal = (estimate) =>
  centsToDollars(estimate?.rows?.find((row) => row.type === "total")?.amount)
